'use client';

import { motion, useInView } from 'motion/react';
import { type FC, type HTMLAttributes, type PropsWithChildren, useMemo, useRef } from 'react';

import { reveal } from '@/components/motion/tokens';
import type { TAnimatedTag, TMotionTag } from '@/lib/interfaces/elements/animatedTag.types';

const STATIC_OFF = Object.freeze({ initial: false, animate: false } as const);

type TagProps = PropsWithChildren<Omit<HTMLAttributes<HTMLDivElement>, 'id'> & TAnimatedTag>;

const Tag: FC<TagProps> = ({ children, tag = 'div', inMotion = true, id, ...props }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  // Keep component type stable across renders
  const MotionTag = useMemo(() => (motion as any)[tag] ?? motion.create<TMotionTag>(tag), [tag]);

  const isInView = useInView(ref, { once: true });
  const motionProps = useMemo(() => (inMotion ? reveal(isInView) : STATIC_OFF), [inMotion, isInView]);

  const elementId = id == null ? undefined : String(id);

  return (
    <MotionTag ref={ref} id={elementId} {...motionProps} {...props}>
      {children}
    </MotionTag>
  );
};

export default Tag;
